import has from "lodash/has";
import { useRotaUsersStore } from "../rotausers";

const API_NAME = "rota";
const API_VERSION = "/v1";
const CONTENT_TYPE = "application/vnd.coactive.rota-user+json";

const MAX_PAGE_SIZE = 250;

const modulestate = () => ({});
const mutations = {};

const actions = {
  async getRotaUser(context, params) {
    // the 'get rotauser' (singular) API endpoint returns the same data as the get rotausers endpoint,
    // so we can use the cached data for speed
    const piniaStore = useRotaUsersStore();
    const rotaUsers = await piniaStore.getRotaUsersForUserSelection();
    const rotaUser = rotaUsers.find((a) => a.userId === params.id);
    if (rotaUser) {
      return rotaUser;
    }
    if (params?.options?.doNotCatchIfError) {
      this.$api.doNotCatchIfError();
    }
    // if not found, fallback to fetch from API
    return this.$api.get(API_NAME, `${API_VERSION}/rota-users/${params.id}`);
  },
  async getRotaUsers(context, payload) {
    const myInit = {};

    // payload contains filter/sort/pagination options.
    if (payload) {
      myInit.queryStringParameters = {
        page: payload.page,
        pageSize: payload.pageSize ? payload.pageSize : MAX_PAGE_SIZE,
      };

      if (has(payload, "sortBy")) {
        let sortField = payload.sortBy;
        if (sortField === "dateCreated") {
          sortField = "_id";
        }
        let sortOrder = 1;
        if (has(payload, "sortDesc") && payload.sortDesc === true) {
          sortOrder = -1;
        }
        myInit.queryStringParameters.sort = JSON.stringify({
          [sortField]: sortOrder,
        });
      }

      if (has(payload, "filter")) {
        myInit.queryStringParameters.filter = JSON.stringify(payload.filter);
      }

      if (has(payload, "archived")) {
        myInit.queryStringParameters.archived = payload.archived;
      }
    }

    return this.$api
      .get(API_NAME, `${API_VERSION}/rota-users`, myInit)
      .then((response) => {
        if (has(response, "_embedded.rota-users")) {
          return response;
        }
        return false;
      });
  },
  /**
   * Cached endpoint to get rota users for use within select dropdowns
   * @param {*} context
   * @returns []
   */
  async getRotaUsersForUserSelection() {
    const piniaStore = useRotaUsersStore();
    return piniaStore.getRotaUsersForUserSelection();
  },

  async createRotaUser(context, rotaUser) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: rotaUser,
    };
    // if no coactive user is linked (e.g. agency) then remove the userId property
    if (rotaUser.userId == null) {
      delete myInit.body.userId;
    }
    const piniaStore = useRotaUsersStore();
    piniaStore.localStorageInvalidateCache("getRotaUsersForUserSelection");
    return this.$api.post(API_NAME, `${API_VERSION}/rota-users`, myInit);
  },

  async updateRotaUser(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: params.changes,
    };
    const piniaStore = useRotaUsersStore();
    piniaStore.localStorageInvalidateCache("getRotaUsersForUserSelection");
    return this.$api.patch(
      API_NAME,
      `${API_VERSION}/rota-users/${params.id}`,
      myInit,
    );
  },

  async getRotaUserAcitivtyPerformanceStats(context, params) {
    if (params.options.doNotCatchIfError) {
      this.$api.doNotCatchIfError();
    }

    const myInit = {
      queryStringParameters: {
        startDate: params.startDate,
        endDate: params.endDate,
        location: params.location,
      },
    };
    return this.$api.get(
      API_NAME,
      `${API_VERSION}/stats/users/activity-performance`,
      myInit,
    );
  },

  async getRotaShiftStaffAgencyHours(context, params) {
    if (params.options.doNotCatchIfError) {
      this.$api.doNotCatchIfError();
    }

    const myInit = {
      queryStringParameters: {
        startDate: params.startDate,
        endDate: params.endDate,
        location: params.location,
      },
    };
    return this.$api.get(
      API_NAME,
      `${API_VERSION}/stats/rotas/shift-hours/staff-agency-split`,
      myInit,
    );
  },

  async getClientAcitivtyPerformanceStats(context, params) {
    if (params.options.doNotCatchIfError) {
      this.$api.doNotCatchIfError();
    }

    const myInit = {
      queryStringParameters: {
        startDate: params.startDate,
        endDate: params.endDate,
        location: params.location,
      },
    };
    return this.$api.get(
      API_NAME,
      `${API_VERSION}/stats/clients/activity-performance`,
      myInit,
    );
  },

  async getRotaUserAuthEvents(context, params) {
    if (params.options.doNotCatchIfError) {
      this.$api.doNotCatchIfError();
    }
    const myInit = {
      queryStringParameters: {
        startDate: params.startDate,
        endDate: params.endDate,
        location: params.location,
      },
    };
    return this.$api.get(
      API_NAME,
      `${API_VERSION}/stats/users/auth-events/day-summary`,
      myInit,
    );
  },

  async latestAuthEventsWithShifts(context, payload) {
    const api = this.$api;
    if (payload.doNotCatchIfError) {
      api.doNotCatchIfError();

      delete payload.doNotCatchIfError;
    }
    const myInit = {
      queryStringParameters: {
        ...payload,
      },
    };
    return api.get(
      API_NAME,
      `${API_VERSION}/stats/users/auth-events/latest-with-shifts`,
      myInit,
    );
  },
};

export default {
  namespaced: true,
  actions,
  mutations,
  state: modulestate,
};
